<template>
    <div>
        <form-select ref="select" v-bind="$attrs" v-model="selection" :aria-label="personBoxAriaLabel" :disabled="inputLocked">
            <template v-for="(_, name) in $slots" :slot="name" slot-scope="slotData">
                <slot :name="name" v-bind="slotData"/>
            </template>
        </form-select>

        <div v-if="persons.length" class="user-container-list">
            <component :is="personBoxType" v-for="id in persons" :id="id" :key="personBoxType + id" :removable="!inputLocked" @remove="remove" class="medium">
                <slot name="person-box" :id="id"/>
            </component>
        </div>
    </div>
</template>

<script lang="ts">
    import {NoOperation} from '@/library/utils';
    import {Component, Prop, Vue, Watch} from 'vue-facing-decorator';
    import FormSelect from './FormSelect.vue';

    type UserSelectValue = number | number[] | undefined | null;

    @Component({
        name: 'FormSelectUser',
        components: {
            FormSelect,
        },
        inheritAttrs: false,
        emits: ['update:modelValue'],
    })
    export class FormSelectUser extends Vue
    {
        readonly $refs: {
            select: FormSelect
        };

        @Prop({
            type: [Array, Number],
        }) modelValue: UserSelectValue;

        @Prop({
            type: Boolean,
            required: false,
        }) inputLocked: boolean;

        @Prop({
            type: Boolean,
        }) focus: boolean;

        @Prop({
            type: Function,
            default: null,
            required: false,
        }) removeCallbackPromise: Nullable<(person: User) => Promise<any>>;

        @Prop({
            type: Boolean,
            default: false,
        }) useEmptyValue: boolean;

        type = 'user';

        selection: UserSelectValue = null;

        persons: number[] = [];

        get personBoxAriaLabel()
        {
            let typeTerm = '';
            let multiple = this.$attrs.multiple;

            if (this.type === 'user')
            {
                typeTerm = 'Nutzer';
            }
            else if (this.type === 'employee')
            {
                typeTerm = 'Mitarbeiter';
            }
            else if (this.type === 'entity')
            {
                typeTerm = 'Abteilung' + (multiple ? 'en' : '');
            }
            else if (this.type === 'role')
            {
                typeTerm = 'Rolle' + (multiple ? 'n' : '');
            }

            return 'Auswahl' + (typeTerm ? ' ' + typeTerm : '');
        }

        get personBoxType()
        {
            return 'person-box-' + this.type;
        }

        @Watch('modelValue')
        watchValue(newValue: UserSelectValue)
        {
            this.selection = newValue;
        }

        @Watch('selection', {deep: true})
        watchSelection(newValue: UserSelectValue)
        {
            this.updatePersons(newValue);
            this.$emit('update:modelValue', newValue);
        }

        created()
        {
            this.selection = this.modelValue || this.useEmptyValue ? this.modelValue : this.selection;

            this.updatePersons(this.selection);
        }

        mounted()
        {
            if (this.focus)
            {
                this.$refs.select.open();
            }
        }

        updatePersons(newValue: UserSelectValue)
        {
            if (!newValue)
            {
                this.persons = [];
            }
            else if (Array.isArray(newValue))
            {
                this.persons = newValue.filter(v => v);
            }
            else
            {
                this.persons = newValue ? [newValue] : [];
            }
        }

        remove(person: User)
        {
            if (this.inputLocked)
            {
                return;
            }

            if (this.removeCallbackPromise)
            {
                this.removeCallbackPromise(person)
                .then(() => this.removePerson(person))
                .catch(NoOperation);
            }
            else
            {
                this.removePerson(person);
            }
        }

        removePerson(person: User)
        {
            if (Array.isArray(this.selection))
            {
                this.selection.splice(this.selection.indexOf(person.id), 1);
            }
            else
            {
                this.selection = null;
            }

            this.$emit('update:modelValue', this.selection);
        }

        getFormElement()
        {
            return this.$refs.select;
        }
    }

    export default FormSelectUser;
</script>